import { useState, useMemo } from "react";
import api, { API } from "@/lib/api";
import { eur, fmtDate } from "@/lib/format";
import { getPeriodRange, inRange } from "@/lib/periods";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { parseAmount } from "@/lib/format";
import { Pencil, Trash2, FileSpreadsheet, FileDown, FileText, ArrowUpDown } from "lucide-react";

const EDIT_FIELDS = [
  { id: "carte_sur_place", label: "Carte — Sur place" },
  { id: "especes_sur_place", label: "Espèces — Sur place" },
  { id: "carte_a_emporter", label: "Carte — À emporter" },
  { id: "especes_a_emporter", label: "Espèces — À emporter" },
];

export default function SalesHistory({ sales, reload, period, custom }) {
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editVals, setEditVals] = useState({});
  const [busy, setBusy] = useState(false);

  const range = useMemo(() => getPeriodRange(period, new Date(), custom), [period, custom]);

  const rows = useMemo(() => {
    let r = sales.filter((s) => inRange(s.date, range.start, range.end));
    if (search) {
      const q = search.toLowerCase().trim();
      r = r.filter((s) => {
        const [y, m, d] = s.date.split("-");
        const ddmmyyyy = `${d}/${m}/${y}`;
        return (
          s.date.includes(q) ||
          ddmmyyyy.includes(q) ||
          fmtDate(s.date).toLowerCase().includes(q)
        );
      });
    }
    r = [...r].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      const cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return r;
  }, [sales, search, sortKey, sortDir, range]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/export/${fmt}`, {
        params: { start: range.start, end: range.end },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      const ext = fmt === "xlsx" ? "xlsx" : fmt === "pdf" ? "pdf" : "csv";
      const base = fmt === "pdf" ? "recap_ventes" : "ventes_lekker";
      a.href = url;
      a.download = `${base}_${range.start}_${range.end}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} téléchargé`);
    } catch {
      toast.error("Échec de l'export");
    }
  };

  const openEdit = (s) => {
    setEditTarget(s);
    setEditVals({
      carte_sur_place: String(s.carte_sur_place).replace(".", ","),
      especes_sur_place: String(s.especes_sur_place).replace(".", ","),
      carte_a_emporter: String(s.carte_a_emporter).replace(".", ","),
      especes_a_emporter: String(s.especes_a_emporter).replace(".", ","),
    });
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/sales/${editTarget.id}`, {
        date: editTarget.date,
        carte_sur_place: parseAmount(editVals.carte_sur_place),
        especes_sur_place: parseAmount(editVals.especes_sur_place),
        carte_a_emporter: parseAmount(editVals.carte_a_emporter),
        especes_a_emporter: parseAmount(editVals.especes_a_emporter),
      });
      toast.success("Saisie mise à jour");
      setEditTarget(null);
      await reload();
    } catch {
      toast.error("Erreur de mise à jour");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/sales/${deleteTarget.id}`);
      toast.success("Saisie supprimée");
      setDeleteTarget(null);
      await reload();
    } catch {
      toast.error("Erreur de suppression");
    } finally {
      setBusy(false);
    }
  };

  const Th = ({ label, k, className = "" }) => (
    <th className={`px-3 py-3 text-xs font-bold uppercase tracking-wider text-[#94A3B8] ${className}`}>
      <button onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 hover:text-white transition-colors">
        {label} <ArrowUpDown className="w-3 h-3" />
      </button>
    </th>
  );

  return (
    <div className="max-w-6xl mx-auto vlf-fade-up" data-testid="sales-history">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Journal des ventes</h2>
          <p className="text-sm text-[#94A3B8]">{range.label} — {rows.length} entrée{rows.length > 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="export-pdf-button"
            onClick={() => download("pdf")}
            className="h-11 px-4 rounded-xl bg-[#FF5A1F] hover:bg-[#E04810] text-white text-sm font-semibold flex items-center gap-2 active:scale-[0.98] transition-all"
          >
            <FileText className="w-4 h-4" /> PDF
          </button>
          <button
            data-testid="export-csv-button"
            onClick={() => download("csv")}
            className="h-11 px-4 rounded-xl bg-[#1A222C] border border-[#253242] text-white text-sm font-semibold flex items-center gap-2 hover:border-[#3A4D66] active:scale-[0.98] transition-all"
          >
            <FileDown className="w-4 h-4" /> CSV
          </button>
          <button
            data-testid="export-excel-button"
            onClick={() => download("xlsx")}
            className="h-11 px-4 rounded-xl bg-[#10B981] hover:bg-[#0e9d70] text-white text-sm font-semibold flex items-center gap-2 active:scale-[0.98] transition-all"
          >
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
        </div>
      </div>

      <input
        data-testid="history-search-input"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Rechercher une date (ex : 15/09/2026 ou septembre)"
        className="w-full h-12 px-4 mb-4 rounded-xl bg-[#131920] border border-[#253242] text-white outline-none focus:border-[#FF5A1F]"
      />

      <div className="rounded-2xl border border-[#253242] overflow-hidden bg-[#131920]">
        <div className="overflow-x-auto">
          <table data-testid="sales-history-table" className="w-full min-w-[720px]">
            <thead className="bg-[#0B0F12] border-b border-[#253242]">
              <tr>
                <Th label="Date" k="date" className="text-left" />
                <Th label="Sur place" k="total_sur_place" className="text-right" />
                <Th label="À emporter" k="total_a_emporter" className="text-right" />
                <Th label="Carte" k="total_carte" className="text-right" />
                <Th label="Espèces" k="total_especes" className="text-right" />
                <Th label="TVA due" k="tva_totale" className="text-right" />
                <Th label="Total" k="total" className="text-right" />
                <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider text-[#94A3B8]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="border-b border-[#1c2530] hover:bg-[#1A222C]/60 transition-colors">
                  <td className="px-3 py-3 text-sm font-medium text-white whitespace-nowrap">{fmtDate(s.date)}</td>
                  <td className="px-3 py-3 text-right font-mono text-sm text-[#F97316]">{eur(s.total_sur_place)}</td>
                  <td className="px-3 py-3 text-right font-mono text-sm text-[#818CF8]">{eur(s.total_a_emporter)}</td>
                  <td className="px-3 py-3 text-right font-mono text-sm text-[#38BDF8]">{eur(s.total_carte)}</td>
                  <td className="px-3 py-3 text-right font-mono text-sm text-[#10B981]">{eur(s.total_especes)}</td>
                  <td className="px-3 py-3 text-right font-mono text-sm text-[#FACC15]">{eur(s.tva_totale)}</td>
                  <td className="px-3 py-3 text-right font-mono text-sm font-bold text-white">{eur(s.total)}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        data-testid={`edit-sale-btn-${s.id}`}
                        onClick={() => openEdit(s)}
                        className="w-9 h-9 rounded-lg bg-[#222D3A] flex items-center justify-center text-[#94A3B8] hover:text-[#38BDF8] active:scale-95 transition-all"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-sale-btn-${s.id}`}
                        onClick={() => setDeleteTarget(s)}
                        className="w-9 h-9 rounded-lg bg-[#222D3A] flex items-center justify-center text-[#94A3B8] hover:text-[#EF4444] active:scale-95 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-10 text-center text-[#64748B]">Aucune saisie sur cette période.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="bg-[#131920] border-[#253242] text-white">
          <DialogHeader>
            <DialogTitle>Modifier — {editTarget ? fmtDate(editTarget.date) : ""}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            {EDIT_FIELDS.map((f) => (
              <div key={f.id}>
                <label className="block text-xs font-semibold text-[#94A3B8] mb-1">{f.label}</label>
                <input
                  data-testid={`edit-input-${f.id.replace(/_/g, "-")}`}
                  inputMode="decimal"
                  value={editVals[f.id] || ""}
                  onChange={(e) => setEditVals((p) => ({ ...p, [f.id]: e.target.value.replace(/[^0-9.,]/g, "") }))}
                  className="w-full h-12 px-3 rounded-lg bg-[#0B0F12] border border-[#253242] font-mono text-white outline-none focus:border-[#FF5A1F]"
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <button
              onClick={() => setEditTarget(null)}
              className="h-11 px-4 rounded-xl bg-[#1A222C] border border-[#253242] text-[#94A3B8] font-semibold"
            >
              Annuler
            </button>
            <button
              data-testid="save-edit-button"
              onClick={saveEdit}
              disabled={busy}
              className="h-11 px-5 rounded-xl bg-[#FF5A1F] hover:bg-[#E04810] text-white font-bold disabled:opacity-50"
            >
              Enregistrer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#131920] border-[#253242] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette saisie ?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#94A3B8]">
              {deleteTarget && `Ventes du ${fmtDate(deleteTarget.date)} (${eur(deleteTarget.total)}). Cette action est irréversible.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1A222C] border-[#253242] text-white hover:bg-[#222D3A] hover:text-white">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-button"
              onClick={confirmDelete}
              disabled={busy}
              className="bg-[#EF4444] hover:bg-[#dc2626] text-white"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
